import { createSlice } from '@reduxjs/toolkit';
// @types
import {
  SettingsBaseSettingsCameraThresholdResponseModel,
  SettingsState,
  BreakTime
} from '../../@types/settings';
import { AxiosResponseModel } from '../../@types/base';
import { HealthCheckStatusData } from '../../@types/healthCheck';
import axios from '../../utils/axios';
import { CONSTS } from './const';
import { dispatch } from '../store';
import { HttpStatusCodes } from '../../constants/http-status-codes.enum';

// ----------------------------------------------------------------------

const initialState: SettingsState = {
  themeMode: 'light',
  themeDirection: 'ltr',
  cameraHealthThreshold: {
    threshold: 60,
    id: 0,
    createdAt: '',
    updatedAt: '',
    companyId: 0
  },
  isSettingsLoading: true,
  breakTimes: [],
  breakTimesError: null,
};

const slice = createSlice({
  name: 'settings',
  initialState,
  reducers: {
    switchMode(state, action) {
      state.themeMode = action.payload;
    },
    switchDirection(state, action) {
      state.themeDirection = action.payload;
    },
    setCameraHealthThreshold(state, action) {
      state.cameraHealthThreshold = action.payload;
    },
    setLoading(state, action) {
      state.isSettingsLoading = action.payload;
    },
    // BREAK TIMES
    hasError(state, action) {
      state.isSettingsLoading = false;
      state.breakTimesError = action.payload;
    },
    getBreakTimesSuccess(state, action) {
      state.isSettingsLoading = false;
      state.breakTimes = action.payload;
    },
  }
});

// Reducer
export default slice.reducer;

export async function getCameraThreshold() {
  dispatch(slice.actions.setLoading(true));
  try {
    const response: AxiosResponseModel<SettingsBaseSettingsCameraThresholdResponseModel> = await axios
      .get(`${CONSTS.SETTINGS_GET_CAMERA_HEALTH_THRESHOLD}`)
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(
        slice.actions.setCameraHealthThreshold(response.data?.data || null)
      );
      dispatch(slice.actions.setLoading(false));
      return true;
    }
    dispatch(slice.actions.setCameraHealthThreshold(60));
    dispatch(slice.actions.setLoading(true));

    return false;
  } catch (error: any) {
    dispatch(slice.actions.setCameraHealthThreshold(60));
    dispatch(slice.actions.setLoading(true));
    return false;
  }
}

export async function setCameraThresholdWithReturn(threshold: number) {
  dispatch(slice.actions.setLoading(true));
  try {
    const response: AxiosResponseModel<HealthCheckStatusData> = await axios
      .post(`${CONSTS.SETTINGS_SET_CAMERA_HEALTH_THRESHOLD}`, {
        threshold
      })
      .catch((error) => {
        if (error.response) {
          return error.response.data;
        }
        return error;
      });
    if (response && response.status === HttpStatusCodes.StatusSuccess) {
      dispatch(slice.actions.setLoading(false));
    } else {
      dispatch(slice.actions.setCameraHealthThreshold(60));
      dispatch(slice.actions.setLoading(true));
    }
    return response.data?.data || null;
  } catch (error: any) {
    dispatch(slice.actions.setCameraHealthThreshold(60));
    dispatch(slice.actions.setLoading(true));
  }
  return null;
}

// Actions
export const { switchMode, switchDirection } = slice.actions;

// ----------------------------------------------------------------------
// BREAK TIMES
// ----------------------------------------------------------------------

export function getBreakTimes() {
  return async () => {
    dispatch(slice.actions.setLoading(true));
    try {
      const response = await axios.get(CONSTS.SETTINGS_BREAK_TIME_LIST);
      dispatch(slice.actions.getBreakTimesSuccess(response.data.data || []));
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function createBreakTime(data: Omit<BreakTime, 'id'>) {
  return async () => {
    dispatch(slice.actions.setLoading(true));
    try {
      await axios.post(CONSTS.SETTINGS_BREAK_TIME_CREATE, data);
      dispatch(getBreakTimes());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function updateBreakTime(id: number, data: Omit<BreakTime, 'id'>) {
  return async () => {
    dispatch(slice.actions.setLoading(true));
    try {
      await axios.put(`${CONSTS.SETTINGS_BREAK_TIME_UPDATE}/${id}`, data);
      dispatch(getBreakTimes());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}

export function deleteBreakTime(id: number) {
  return async () => {
    dispatch(slice.actions.setLoading(true));
    try {
      await axios.delete(`${CONSTS.SETTINGS_BREAK_TIME_DELETE}/${id}`);
      dispatch(getBreakTimes());
    } catch (error) {
      dispatch(slice.actions.hasError(error));
    }
  };
}
